import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";

/** Icona occhio ultra-minimale (stroke 1.25, no fill) */
const EyeMini = ({ shown }) => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 10 Q10 3.5 18.5 10 Q10 16.5 1.5 10 Z" />
    {shown ? <circle cx="10" cy="10" r="1.5" /> : <line x1="4" y1="4" x2="16" y2="16" />}
  </svg>
);

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bentornato!", { id: "login-welcome" });
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center mb-8" data-testid="login-brand-link">
          <Logo iconSize={36} textClassName="text-xl" />
        </Link>
        <Card className="p-8 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h1 className="font-heading font-bold text-3xl mb-2">Bentornato</h1>
          <p className="text-muted-foreground mb-6">Accedi al tuo account</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl mt-1.5" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password">Password</Label>
                <Link to="/reset-password" className="text-xs text-primary font-semibold hover:underline" data-testid="login-forgot-link">
                  Password dimenticata?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl pr-11"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  data-testid="login-toggle-password"
                >
                  <EyeMini shown={showPassword} />
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full hover:-translate-y-0.5 transition-transform" data-testid="login-submit-button">
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Non hai un account? <Link to="/register" className="text-primary font-semibold" data-testid="login-to-register">Registrati</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
